/**
 * Centralized Frontend Application Configuration
 *
 * Single source of truth for all configuration values.
 * Ensures dev always uses dev URLs, prod uses prod URLs.
 * All values loaded from environment variables.
 */

// ============================================
// ENVIRONMENT DETECTION
// ============================================

const isDevelopment = import.meta.env.MODE === 'development' || import.meta.env.DEV;
const isProduction = import.meta.env.MODE === 'production' || import.meta.env.PROD;

// ============================================
// API CONFIGURATION
// ============================================

export const API_CONFIG = {
  // Base API URL - NO FALLBACK TO PROD!
  baseUrl: import.meta.env.VITE_API_URL || (isDevelopment ? 'http://localhost:3000/api' : ''),

  // WebSocket URL
  wsUrl: import.meta.env.VITE_WS_URL || (isDevelopment ? 'ws://localhost:3000' : ''),

  // Timeout
  timeout: 30000, // 30 seconds
} as const;

// Validation: Ensure API URL is set
if (!API_CONFIG.baseUrl) {
  console.error('VITE_API_URL is not set! Application will not work correctly.');
}

// ============================================
// FRONTEND URL (for referral links, etc.)
// ============================================

export const FRONTEND_CONFIG = {
  // Base URL - NO FALLBACK TO PROD!
  baseUrl: import.meta.env.VITE_FRONTEND_URL || (isDevelopment ? 'http://localhost:5173' : ''),

  // Domain (without protocol)
  domain: (import.meta.env.VITE_FRONTEND_URL || 'localhost:5173').replace(/^https?:\/\//, ''),
} as const;

// Validation: Ensure frontend URL is set
if (!FRONTEND_CONFIG.baseUrl && isProduction) {
  console.error('VITE_FRONTEND_URL is not set in production!');
}

// ============================================
// BLOCKCHAIN CONFIGURATION
// ============================================

export const BLOCKCHAIN_CONFIG = {
  // Solana Network (devnet by default)
  solana: {
    network: import.meta.env.VITE_SOLANA_NETWORK || 'mainnet-beta',
    rpcUrl: `${API_CONFIG.baseUrl}/solana/rpc`,
    wsUrl: import.meta.env.VITE_SOLANA_WS_URL || 'wss://api.mainnet-beta.solana.com/',
  },

  // Token Mint Addresses (MAINNET by default)
  tokens: {
    usdt: import.meta.env.VITE_USDT_MINT || 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB',
    takara: import.meta.env.VITE_TAKARA_MINT || '8e2ieyBCgdx3254SBtVy5e3vuqgrK5C8Yq6UGt3DpGMq',
    laika: import.meta.env.VITE_LAIKA_MINT || 'Euoq6CyQFCjCVSLR9wFaUPDW19Y6ZHwEcJoZsEi643i1',
  },

  // Contract addresses
  contract: {
    programId: import.meta.env.VITE_TAKARA_PROGRAM_ID || 'AJfdkSvXJ5JMZ5RUg45XH3gb4AihiY3L1g1u7wtq13M3',
    poolPda: import.meta.env.VITE_TAKARA_POOL_PDA || '3P9AaMrzmmi4s4rKNRP2YoqQM7oc42etwbwd62FC6aSF',
    globalStatePda: import.meta.env.VITE_TAKARA_GLOBAL_STATE_PDA || 'J9nFAo5gUBrYdGHSWEhyKx6GSVcSNctTDhPbbjzpQXxR',
    poolUsdtAta: import.meta.env.VITE_TAKARA_POOL_USDT_ATA || 'AHeLtNwE2ERhcjibisREVKJBa2hyUhxGnpMr3xfQDawe',
    poolTakaraAta: import.meta.env.VITE_TAKARA_POOL_TAKARA_ATA || 'EPpQoPT9GjMitnk5BBRdtgRuKd8nAMuhdrizSRHCCK3h',
    operatorPubkey: import.meta.env.VITE_TAKARA_OPERATOR_PUBKEY || '9akH391wEXgjpxpyULJ4xxJUYQvZf3vFRBgsJt9y1w9V',
  },

  // Platform Wallet (MUST be set - no fallback to prevent sending to wrong address!)
  platformWallet: (() => {
    const wallet = import.meta.env.VITE_PLATFORM_WALLET_SOL;
    if (!wallet) {
      throw new Error(
        'CRITICAL: VITE_PLATFORM_WALLET_SOL environment variable is not set. ' +
        'This is required to receive payments. Check your .env file.'
      );
    }
    return wallet;
  })(),
} as const;

// ============================================
// EXPLORER CONFIGURATION
// ============================================

export const EXPLORER_CONFIG = {
  // Base URL
  baseUrl: import.meta.env.VITE_SOLSCAN_BASE_URL || 'https://solscan.io',

  // Network suffix
  networkSuffix: BLOCKCHAIN_CONFIG.solana.network === 'devnet' ? '?cluster=devnet' : '',

  // Helper functions
  getTransactionUrl: (signature: string) => {
    const suffix = BLOCKCHAIN_CONFIG.solana.network === 'devnet' ? '?cluster=devnet' : '';
    return `${EXPLORER_CONFIG.baseUrl}/tx/${signature}${suffix}`;
  },

  getTokenUrl: (mint: string) => {
    const suffix = BLOCKCHAIN_CONFIG.solana.network === 'devnet' ? '?cluster=devnet' : '';
    return `${EXPLORER_CONFIG.baseUrl}/token/${mint}${suffix}`;
  },

  getAccountUrl: (address: string) => {
    const suffix = BLOCKCHAIN_CONFIG.solana.network === 'devnet' ? '?cluster=devnet' : '';
    return `${EXPLORER_CONFIG.baseUrl}/account/${address}${suffix}`;
  },
} as const;

// ============================================
// EXTERNAL LINKS
// ============================================

export const EXTERNAL_LINKS = {
  // Documentation
  docs: import.meta.env.VITE_GITBOOK_URL || 'https://takara-finance.gitbook.io/takara-docs/',

  // Social Media
  social: {
    instagram: import.meta.env.VITE_INSTAGRAM_URL || 'https://www.instagram.com/takarafinance/',
    twitter: import.meta.env.VITE_TWITTER_URL || 'https://x.com/TakaraFinance',
    telegram: import.meta.env.VITE_TELEGRAM_URL || 'https://t.me/TakaraFinance',
  },

  // Wallet
  phantomWallet: 'https://phantom.app/',
} as const;

// ============================================
// APPLICATION CONFIGURATION
// ============================================

export const APP_CONFIG = {
  name: import.meta.env.VITE_APP_NAME || 'Takara Gold',
  version: import.meta.env.VITE_APP_VERSION || '3.0.0',

  // Feature Flags
  enableDebug: import.meta.env.VITE_ENABLE_DEBUG === 'true' || isDevelopment,
  simulationMode: import.meta.env.VITE_SIMULATION_MODE === 'true',

  // Vault Launch Date
  vaultLaunchDate: import.meta.env.VITE_VAULT_LAUNCH_DATE || '2025-02-05T16:00:00+04:00',
} as const;

// ============================================
// UI CONFIGURATION
// ============================================

export const UI_CONFIG = {
  // Timeouts
  toasts: {
    successDuration: 3000,
    errorDuration: 5000,
    copyDuration: 2000,
  },

  // Polling Intervals
  polling: {
    balanceRefresh: 30000, // 30 seconds
    transactionCheck: 5000, // 5 seconds (reduced from 1.5s for lower RPC load)
    phantomCheck: 1000, // 1 second
  },

  // Auto-connect
  autoConnect: {
    timeout: 500, // 500ms
    adminTimeout: 1000, // 1 second for admin
  },

  // Query staleTime (React Query)
  staleTime: {
    vaults: 5 * 60 * 1000, // 5 minutes
    investments: 5 * 60 * 1000, // 5 minutes
    portfolio: 1 * 60 * 1000, // 1 minute
    marketplace: 30 * 1000, // 30 seconds
    claims: 1 * 60 * 1000, // 1 minute
    referrals: 30 * 1000, // 30 seconds
    tokenomics: 10 * 60 * 1000, // 10 minutes
    balances: 15 * 1000, // 15 seconds
    admin: 1 * 60 * 1000, // 1 minute
  },
} as const;

// ============================================
// SENTRY CONFIGURATION
// ============================================

export const SENTRY_CONFIG = {
  dsn: import.meta.env.VITE_SENTRY_DSN || 'https://4466ed8c14a827b857ee17c865260337@o4510398409670656.ingest.us.sentry.io/4510599662665728',

  environment: isDevelopment ? 'development' : 'production',
  release: APP_CONFIG.version,

  // Sampling
  tracesSampleRate: isDevelopment ? 0.5 : 0.1,
  sessionReplaySampleRate: isDevelopment ? 0.3 : 0.1,
  errorReplaySampleRate: 1.0,
  sampleRate: 1.0,

  // Integrations
  integrations: ['BrowserTracing', 'Replay'],
} as const;

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Check if running in development mode
 */
export const isDev = () => isDevelopment;

/**
 * Check if running in production mode
 */
export const isProd = () => isProduction;

/**
 * Check if on devnet
 */
export const isDevnet = () => BLOCKCHAIN_CONFIG.solana.network === 'devnet';

/**
 * Check if on mainnet
 */
export const isMainnet = () => BLOCKCHAIN_CONFIG.solana.network === 'mainnet-beta';

/**
 * Get full API endpoint URL
 */
export const getApiUrl = (endpoint: string) => {
  const base = API_CONFIG.baseUrl.endsWith('/') ? API_CONFIG.baseUrl.slice(0, -1) : API_CONFIG.baseUrl;
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${base}${path}`;
};

/**
 * Get referral link
 */
export const getReferralLink = (code: string) => {
  return `${FRONTEND_CONFIG.baseUrl}/?ref=${code}`;
};

/**
 * Get referral link display (without protocol)
 */
export const getReferralLinkDisplay = (code: string) => {
  return `${FRONTEND_CONFIG.domain}/?ref=${code}`;
};

// ============================================
// VALIDATION & WARNINGS
// ============================================

// Log configuration in development
if (isDevelopment) {
  console.log('🔧 App Configuration:', {
    mode: isDevelopment ? 'development' : 'production',
    api: API_CONFIG.baseUrl,
    frontend: FRONTEND_CONFIG.baseUrl,
    network: BLOCKCHAIN_CONFIG.solana.network,
    rpc: BLOCKCHAIN_CONFIG.solana.rpcUrl,
  });
}

// Warn if using production URLs in development
if (isDevelopment && API_CONFIG.baseUrl.includes('takarafi.com')) {
  console.warn('⚠️ WARNING: Using production API URL in development mode!');
}

if (isDevelopment && FRONTEND_CONFIG.baseUrl.includes('takarafi.com')) {
  console.warn('⚠️ WARNING: Using production frontend URL in development mode!');
}

// Error if production is using dev URLs
if (isProduction && (API_CONFIG.baseUrl.includes('localhost') || API_CONFIG.baseUrl.includes('oadno.com'))) {
  console.error('❌ ERROR: Production is using development/staging API URL!');
}

if (isProduction && (FRONTEND_CONFIG.baseUrl.includes('localhost') || FRONTEND_CONFIG.baseUrl.includes('oadno.com'))) {
  console.error('❌ ERROR: Production is using development/staging frontend URL!');
}
